/**
 * Background Agent Manager
 * Manages agents running in the background while user continues working
 * Inspired by Claude Code's background agent feature
 */

import { EventEmitter } from 'events';
import fs from 'fs';
import path from 'path';
import { fork, ChildProcess } from 'child_process';

export interface BackgroundTask {
  /** Unique task ID */
  id: string;

  /** Task description/prompt */
  prompt: string;

  /** Current status */
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

  /** When the task was created */
  createdAt: string;

  /** When the task started running */
  startedAt?: string;

  /** When the task completed */
  completedAt?: string;

  /** Task result/output */
  result?: string;

  /** Error message if failed */
  error?: string;

  /** Progress percentage (0-100) */
  progress?: number;

  /** Progress message */
  progressMessage?: string;

  /** Child process PID */
  pid?: number;

  /** Session ID */
  sessionId: string;

  /** Agent type */
  agentType?: string;

  /** Metadata */
  metadata?: Record<string, any>;
}

export interface BackgroundAgentConfig {
  /** Maximum concurrent background tasks */
  maxConcurrent: number;

  /** Default timeout for background tasks (ms) */
  defaultTimeout: number;

  /** Whether to auto-start pending tasks */
  autoStart: boolean;

  /** Directory for task persistence */
  tasksDir: string;

  /** Wake-up notification enabled */
  notifyOnComplete: boolean;
}

const DEFAULT_CONFIG: BackgroundAgentConfig = {
  maxConcurrent: 3,
  defaultTimeout: 600000, // 10 minutes
  autoStart: true,
  tasksDir: '.metis/background-tasks',
  notifyOnComplete: true,
};

export class BackgroundAgentManager extends EventEmitter {
  private tasks: Map<string, BackgroundTask> = new Map();
  private runningProcesses: Map<string, ChildProcess> = new Map();
  private config: BackgroundAgentConfig;
  private projectRoot: string;
  private sessionId: string;
  private taskQueue: string[] = [];

  constructor(
    projectRoot: string,
    sessionId: string,
    config?: Partial<BackgroundAgentConfig>
  ) {
    super();
    this.projectRoot = projectRoot;
    this.sessionId = sessionId;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.config.tasksDir = path.join(projectRoot, this.config.tasksDir);

    this.ensureTasksDirectory();
    this.loadTasks();
  }

  /**
   * Ensure tasks directory exists
   */
  private ensureTasksDirectory(): void {
    if (!fs.existsSync(this.config.tasksDir)) {
      fs.mkdirSync(this.config.tasksDir, { recursive: true });
    }
  }

  /**
   * Load persisted tasks
   */
  private loadTasks(): void {
    const indexPath = path.join(this.config.tasksDir, 'index.json');

    if (fs.existsSync(indexPath)) {
      try {
        const data = fs.readFileSync(indexPath, 'utf8');
        const tasks = JSON.parse(data) as BackgroundTask[];

        for (const task of tasks) {
          this.tasks.set(task.id, task);

          // Re-queue pending tasks
          if (task.status === 'pending' || task.status === 'running') {
            task.status = 'pending'; // Reset running tasks to pending
            this.taskQueue.push(task.id);
          }
        }

        // Auto-start if enabled
        if (this.config.autoStart) {
          this.processQueue();
        }
      } catch (error) {
        console.warn('Failed to load background tasks:', error);
      }
    }
  }

  /**
   * Save tasks index
   */
  private saveTasks(): void {
    const indexPath = path.join(this.config.tasksDir, 'index.json');
    const tasks = Array.from(this.tasks.values());
    fs.writeFileSync(indexPath, JSON.stringify(tasks, null, 2));
  }

  /**
   * Save individual task
   */
  private saveTask(task: BackgroundTask): void {
    const taskPath = path.join(this.config.tasksDir, `${task.id}.json`);
    fs.writeFileSync(taskPath, JSON.stringify(task, null, 2));
    this.saveTasks();
  }

  /**
   * Create a new background task
   */
  createTask(
    prompt: string,
    options: {
      agentType?: string;
      metadata?: Record<string, any>;
    } = {}
  ): BackgroundTask {
    const id = `bg-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    const task: BackgroundTask = {
      id,
      prompt,
      status: 'pending',
      createdAt: new Date().toISOString(),
      sessionId: this.sessionId,
      agentType: options.agentType,
      metadata: options.metadata,
    };

    this.tasks.set(id, task);
    this.taskQueue.push(id);
    this.saveTask(task);

    this.emit('task:created', task);

    // Auto-start if enabled
    if (this.config.autoStart) {
      this.processQueue();
    }

    return task;
  }

  /**
   * Process the task queue
   */
  private processQueue(): void {
    // Count running tasks
    const runningCount = Array.from(this.tasks.values()).filter(
      t => t.status === 'running'
    ).length;

    // Start tasks up to max concurrent
    while (
      this.taskQueue.length > 0 &&
      runningCount < this.config.maxConcurrent
    ) {
      const taskId = this.taskQueue.shift();
      if (taskId) {
        this.startTask(taskId);
      }
    }
  }

  /**
   * Start a task
   */
  private async startTask(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'pending') return;

    task.status = 'running';
    task.startedAt = new Date().toISOString();
    this.saveTask(task);

    this.emit('task:started', task);

    try {
      // Execute the task
      const result = await this.executeTask(task);

      task.status = 'completed';
      task.completedAt = new Date().toISOString();
      task.result = result;
      this.saveTask(task);

      this.emit('task:completed', task);

      // Notify user
      if (this.config.notifyOnComplete) {
        this.notifyCompletion(task);
      }
    } catch (error) {
      task.status = 'failed';
      task.completedAt = new Date().toISOString();
      task.error = error instanceof Error ? error.message : String(error);
      this.saveTask(task);

      this.emit('task:failed', task);
    }

    // Process next in queue
    this.processQueue();
  }

  /**
   * Execute a task (placeholder for actual implementation)
   */
  private async executeTask(task: BackgroundTask): Promise<string> {
    // This would integrate with the actual agent execution
    // For now, we'll create a simple simulation

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Task timeout'));
      }, this.config.defaultTimeout);

      // Simulate task execution with progress updates
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        task.progress = Math.min(progress, 90);
        task.progressMessage = `Processing... ${progress}%`;
        this.emit('task:progress', task);
      }, 1000);

      // In real implementation, this would:
      // 1. Fork a child process or worker
      // 2. Run the agent with the prompt
      // 3. Collect and return results

      // Simulate completion after 5 seconds
      setTimeout(() => {
        clearTimeout(timeout);
        clearInterval(progressInterval);
        task.progress = 100;
        resolve(`Task completed: ${task.prompt.substring(0, 50)}...`);
      }, 5000);
    });
  }

  /**
   * Notify user of task completion
   */
  private notifyCompletion(task: BackgroundTask): void {
    // Emit notification event
    this.emit('notification', {
      type: task.status === 'completed' ? 'success' : 'error',
      title: `Background Task ${task.status === 'completed' ? 'Completed' : 'Failed'}`,
      message: task.status === 'completed'
        ? `Task "${task.prompt.substring(0, 30)}..." finished successfully`
        : `Task "${task.prompt.substring(0, 30)}..." failed: ${task.error}`,
      taskId: task.id,
    });

    // Could also use system notifications here
    // e.g., node-notifier for desktop notifications
  }

  /**
   * Get task by ID
   */
  getTask(taskId: string): BackgroundTask | undefined {
    return this.tasks.get(taskId);
  }

  /**
   * Get all tasks
   */
  getAllTasks(): BackgroundTask[] {
    return Array.from(this.tasks.values());
  }

  /**
   * Get tasks by status
   */
  getTasksByStatus(status: BackgroundTask['status']): BackgroundTask[] {
    return Array.from(this.tasks.values()).filter(t => t.status === status);
  }

  /**
   * Get tasks for current session
   */
  getSessionTasks(): BackgroundTask[] {
    return Array.from(this.tasks.values()).filter(
      t => t.sessionId === this.sessionId
    );
  }

  /**
   * Cancel a task
   */
  cancelTask(taskId: string): boolean {
    const task = this.tasks.get(taskId);
    if (!task) return false;

    // Kill process if running
    const process = this.runningProcesses.get(taskId);
    if (process) {
      process.kill('SIGTERM');
      this.runningProcesses.delete(taskId);
    }

    // Remove from queue if pending
    const queueIndex = this.taskQueue.indexOf(taskId);
    if (queueIndex !== -1) {
      this.taskQueue.splice(queueIndex, 1);
    }

    task.status = 'cancelled';
    task.completedAt = new Date().toISOString();
    this.saveTask(task);

    this.emit('task:cancelled', task);
    return true;
  }

  /**
   * Delete a task
   */
  deleteTask(taskId: string): boolean {
    // Cancel if running
    this.cancelTask(taskId);

    // Delete from tasks
    const deleted = this.tasks.delete(taskId);

    // Delete task file
    const taskPath = path.join(this.config.tasksDir, `${taskId}.json`);
    if (fs.existsSync(taskPath)) {
      fs.unlinkSync(taskPath);
    }

    this.saveTasks();
    return deleted;
  }

  /**
   * Clear completed tasks
   */
  clearCompletedTasks(): number {
    let cleared = 0;
    for (const [id, task] of this.tasks) {
      if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
        this.deleteTask(id);
        cleared++;
      }
    }
    return cleared;
  }

  /**
   * Get queue status
   */
  getQueueStatus(): {
    pending: number;
    running: number;
    completed: number;
    failed: number;
    total: number;
  } {
    const tasks = Array.from(this.tasks.values());
    return {
      pending: tasks.filter(t => t.status === 'pending').length,
      running: tasks.filter(t => t.status === 'running').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      failed: tasks.filter(t => t.status === 'failed').length,
      total: tasks.length,
    };
  }

  /**
   * Check if prompt should run in background (starts with &)
   */
  static isBackgroundPrompt(prompt: string): boolean {
    return prompt.trim().startsWith('&');
  }

  /**
   * Strip background prefix from prompt
   */
  static stripBackgroundPrefix(prompt: string): string {
    return prompt.trim().replace(/^&\s*/, '');
  }

  /**
   * Shutdown manager - cancel all running tasks
   */
  shutdown(): void {
    for (const taskId of this.runningProcesses.keys()) {
      this.cancelTask(taskId);
    }
  }
}

// Global background agent manager instance
let globalBackgroundAgentManager: BackgroundAgentManager | null = null;

export function getBackgroundAgentManager(
  projectRoot?: string,
  sessionId?: string
): BackgroundAgentManager {
  if (!globalBackgroundAgentManager) {
    globalBackgroundAgentManager = new BackgroundAgentManager(
      projectRoot || process.cwd(),
      sessionId || `session-${Date.now()}`
    );
  }
  return globalBackgroundAgentManager;
}

export function resetBackgroundAgentManager(): void {
  if (globalBackgroundAgentManager) {
    globalBackgroundAgentManager.shutdown();
    globalBackgroundAgentManager = null;
  }
}
